<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

/**
 * Shared list footer: range info, windowed page numbers, prev/next and an
 * optional page-size selector. Works for both server-paged lists (purchase
 * orders) and client-side sliced lists (deliveries, supplier invoices).
 */
const props = withDefaults(
  defineProps<{
    page: number
    totalPages: number
    total: number
    perPage: number
    /** Page-size choices; a single entry hides the selector. */
    sizeOptions?: number[]
  }>(),
  { sizeOptions: () => [20, 50, 100] },
)

const emit = defineEmits<{
  'update:page': [page: number]
  'update:perPage': [perPage: number]
}>()

const { t } = useI18n()

const lastPage = computed(() => Math.max(1, props.totalPages))
const from = computed(() => (props.total === 0 ? 0 : (props.page - 1) * props.perPage + 1))
const to = computed(() => Math.min(props.page * props.perPage, props.total))

/** `1 … 4 5 6 … 12` — a small window around the current page. */
const pages = computed<Array<number | 'gap'>>(() => {
  const last = lastPage.value
  if (last <= 7) {
    return Array.from({ length: last }, (_, index) => index + 1)
  }

  const items: Array<number | 'gap'> = [1]
  const start = Math.max(2, props.page - 1)
  const end = Math.min(last - 1, props.page + 1)

  if (start > 2) items.push('gap')
  for (let index = start; index <= end; index += 1) items.push(index)
  if (end < last - 1) items.push('gap')
  items.push(last)

  return items
})

function go(page: number) {
  if (page < 1 || page > lastPage.value || page === props.page) return
  emit('update:page', page)
}

function changeSize(event: Event) {
  const size = Number((event.target as HTMLSelectElement).value)
  if (Number.isFinite(size) && size > 0) emit('update:perPage', size)
}
</script>

<template>
  <div class="list-pagination">
    <span class="list-pagination__range">
      {{ t('procurement.pagination.range', { from, to, total }) }}
    </span>

    <div class="list-pagination__pages">
      <button
        class="page-nav"
        type="button"
        :disabled="page <= 1"
        :aria-label="t('procurement.pagination.previous')"
        @click="go(page - 1)"
      >
        <ChevronLeft :size="14" aria-hidden="true" />
      </button>

      <template v-for="(item, index) in pages" :key="`${item}-${index}`">
        <span v-if="item === 'gap'" class="page-gap" aria-hidden="true">…</span>
        <button
          v-else
          class="page-number"
          :class="{ 'page-number--active': item === page }"
          :aria-current="item === page ? 'page' : undefined"
          type="button"
          @click="go(item)"
        >
          {{ item }}
        </button>
      </template>

      <button
        class="page-nav"
        type="button"
        :disabled="page >= lastPage"
        :aria-label="t('procurement.pagination.next')"
        @click="go(page + 1)"
      >
        <ChevronRight :size="14" aria-hidden="true" />
      </button>
    </div>

    <label v-if="sizeOptions.length > 1" class="list-pagination__size">
      <span>{{ t('procurement.pagination.size') }}</span>
      <select :value="perPage" @change="changeSize">
        <option v-for="size in sizeOptions" :key="size" :value="size">{{ size }}</option>
      </select>
    </label>
  </div>
</template>
